package dev.lingvo.site.components.studio.editor

import dev.lingvo.shared.screenshots.doc.Point
import dev.lingvo.shared.screenshots.geom.Rect
import kotlin.math.exp
import kotlin.math.min

/** CSS-space viewport math stays independent of browser layout reads and Compose. */
internal data class StageViewport(
    val width: Double, val height: Double, val documentWidth: Double, val documentHeight: Double,
    val zoom: Double = 1.0, val pan: Point = Point(0.0, 0.0), val dpr: Double = 1.0,
) {
    init {
        require(listOf(width, height, documentWidth, documentHeight, zoom, dpr).all { it.isFinite() && it > 0 })
        require(pan.x.isFinite() && pan.y.isFinite())
        require(zoom in .05..8.0)
    }
    val pixelRatio: Double get() = dpr.coerceIn(1.0, 2.0)
    /** Refit after host/panel geometry changes; observer echoes and DPR changes retain user zoom. */
    fun resized(width: Double, height: Double, dpr: Double = this.dpr): StageViewport {
        val next = copy(width = width, height = height, dpr = dpr)
        return if (width != this.width || height != this.height) next.fit() else next
    }
    fun toDocument(point: Point, backingPixels: Boolean = false): Point {
        val ratio = if (backingPixels) pixelRatio else 1.0
        return Point((point.x / ratio - pan.x) / zoom, (point.y / ratio - pan.y) / zoom)
    }
    fun toScreen(point: Point, backingPixels: Boolean = false): Point {
        val ratio = if (backingPixels) pixelRatio else 1.0
        return Point((point.x * zoom + pan.x) * ratio, (point.y * zoom + pan.y) * ratio)
    }
    fun zoomAbout(pointer: Point, nextZoom: Double): StageViewport {
        require(nextZoom.isFinite() && nextZoom > 0)
        val fixed = toDocument(pointer)
        val next = nextZoom.coerceIn(.05, 8.0)
        return copy(zoom = next, pan = Point(pointer.x - fixed.x * next, pointer.y - fixed.y * next))
    }
    fun wheelZoom(pointer: Point, deltaY: Double): StageViewport =
        zoomAbout(pointer, (zoom * exp((-deltaY * .01).coerceIn(-20.0, 20.0))).coerceIn(.05, 8.0))
    fun panBy(delta: Point): StageViewport = copy(pan = Point(
        (pan.x + delta.x).coerceIn(min(64.0, documentWidth * zoom) - documentWidth * zoom, width - min(64.0, width)),
        (pan.y + delta.y).coerceIn(min(64.0, documentHeight * zoom) - documentHeight * zoom, height - min(64.0, height)),
    ))
    fun fit(padding: Double = 24.0): StageViewport = fitRect(Rect(0.0, 0.0, documentWidth, documentHeight), padding)
    fun fitRect(rect: Rect, padding: Double = 24.0): StageViewport {
        require(rect.w > 0 && rect.h > 0 && padding >= 0)
        val next = min((width - 2 * padding) / rect.w, (height - 2 * padding) / rect.h).coerceIn(.05, 8.0)
        return copy(zoom = next, pan = Point((width - rect.w * next) / 2 - rect.x * next,
            (height - rect.h * next) / 2 - rect.y * next))
    }
}
